class ZerokitHowSlider {
    constructor(root) {
        this.root = root;
        this.track = root.querySelector('[data-zk-how-track]');
        this.slides = Array.from(root.querySelectorAll('[data-zk-how-slide]'));
        this.dots = Array.from(root.querySelectorAll('[data-zk-how-dot]'));

        if (!this.track || this.slides.length <= 1) {
            return;
        }

        this.loop = root.dataset.loop !== 'false';
        this.autoplayEnabled = root.dataset.autoplay !== 'false';
        this.interval = Number.parseInt(root.dataset.interval, 10) || 5000;

        this.currentIndex = 0;

        this.isDragging = false;
        this.isAnimating = false;

        this.startX = 0;
        this.currentX = 0;
        this.dragDistance = 0;

        this.autoplayTimer = null;
        this.isInView = true;

        this.prefersReducedMotion = window.matchMedia(
            '(prefers-reduced-motion: reduce)'
        );

        this.pointerId = null;

        this.handlePointerDown = this.handlePointerDown.bind(this);
        this.handlePointerMove = this.handlePointerMove.bind(this);
        this.handlePointerUp = this.handlePointerUp.bind(this);
        this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
        this.handleMouseEnter = this.handleMouseEnter.bind(this);
        this.handleMouseLeave = this.handleMouseLeave.bind(this);
        this.handleFocusIn = this.handleFocusIn.bind(this);
        this.handleFocusOut = this.handleFocusOut.bind(this);
        this.handleTransitionEnd = this.handleTransitionEnd.bind(this);

        this.init();
    }

    init() {
        this.setupLoop();
        this.update(false);
        this.bindEvents();
        this.observeVisibility();
        this.startAutoplay();
    }

    bindEvents() {
        this.root.addEventListener('pointerdown', this.handlePointerDown);

        window.addEventListener('pointermove', this.handlePointerMove, {
            passive: false,
        });

        window.addEventListener('pointerup', this.handlePointerUp);
        window.addEventListener('pointercancel', this.handlePointerUp);

        this.root.addEventListener('mouseenter', this.handleMouseEnter);
        this.root.addEventListener('mouseleave', this.handleMouseLeave);

        this.root.addEventListener('focusin', this.handleFocusIn);
        this.root.addEventListener('focusout', this.handleFocusOut);

        document.addEventListener(
            'visibilitychange',
            this.handleVisibilityChange
        );

        this.track.addEventListener('transitionend', this.handleTransitionEnd);

        this.dots.forEach((dot) => {
            dot.addEventListener('click', () => {
                const index = Number.parseInt(dot.dataset.slideIndex, 10);

                if (Number.isNaN(index)) {
                    return;
                }

                this.goTo(index);
                this.restartAutoplay();
            });
        });
    }

    handlePointerDown(event) {
        if (
            event.pointerType === 'mouse' &&
            event.button !== 0
        ) {
            return;
        }

        if (event.target.closest('[data-zk-how-dot]')) {
            return;
        }

        this.isDragging = true;
        this.isAnimating = false;

        this.pointerId = event.pointerId;

        this.startX = event.clientX;
        this.currentX = event.clientX;
        this.dragDistance = 0;

        this.root.classList.add('is-dragging');

        if (this.root.setPointerCapture) {
            try {
                this.root.setPointerCapture(event.pointerId);
            } catch (error) {
                // Pointer capture is optional.
            }
        }

        this.stopAutoplay();
    }

    handlePointerMove(event) {
        if (!this.isDragging) {
            return;
        }

        if (
            this.pointerId !== null &&
            event.pointerId !== this.pointerId
        ) {
            return;
        }

        this.currentX = event.clientX;
        this.dragDistance = this.currentX - this.startX;

        if (Math.abs(this.dragDistance) > 8) {
            event.preventDefault();
        }

        const width = this.root.clientWidth;

        if (!width) {
            return;
        }

        const basePercent = -(this.physicalIndex * 100);
        const dragPercent = (this.dragDistance / width) * 100;

        this.track.style.transform =
            `translate3d(${basePercent + dragPercent}%, 0, 0)`;
    }

    handlePointerUp(event) {
        if (!this.isDragging) {
            return;
        }

        if (
            this.pointerId !== null &&
            event.pointerId !== this.pointerId
        ) {
            return;
        }

        this.isDragging = false;
        this.pointerId = null;

        this.root.classList.remove('is-dragging');

        const width = this.root.clientWidth;
        const threshold = Math.min(
            Math.max(width * 0.15, 48),
            120
        );

        if (this.dragDistance <= -threshold) {
            this.next();
        } else if (this.dragDistance >= threshold) {
            this.previous();
        } else {
            this.update();
        }

        this.dragDistance = 0;

        this.startAutoplay();
    }

    next() {
        if (this.loop) {
            this.currentIndex = (this.currentIndex + 1) % this.slides.length;
            this.physicalIndex += 1;
            this.update();
            return;
        }
        this.goTo(Math.min(this.currentIndex + 1, this.slides.length - 1));
    }

    previous() {
        if (this.loop) {
            this.currentIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
            this.physicalIndex -= 1;
            this.update();
            return;
        }
        this.goTo(Math.max(this.currentIndex - 1, 0));
    }

    goTo(index, animate = true) {
        if (index < 0 || index >= this.slides.length) {
            return;
        }

        this.currentIndex = index;
        this.physicalIndex = this.loop ? index + 1 : index;
        this.update(animate);
    }

    update(animate = true) {
        if (!animate) {
            this.track.style.transition = 'none';
        } else {
            this.track.style.removeProperty('transition');
        }

        const offset = -(this.physicalIndex * 100);

        this.track.style.transform =
            `translate3d(${offset}%, 0, 0)`;

        this.slides.forEach((slide, index) => {
            const active = index === this.currentIndex;

            slide.classList.toggle('is-active', active);
            slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        });

        this.dots.forEach((dot, index) => {
            const active = index === this.currentIndex;

            dot.classList.toggle('is-active', active);

            if (active) {
                dot.setAttribute('aria-current', 'true');
            } else {
                dot.removeAttribute('aria-current');
            }
        });

        if (!animate) {
            requestAnimationFrame(() => {
                this.track.style.removeProperty('transition');
            });
        }
    }

    setupLoop() {
        this.physicalIndex = this.loop ? 1 : 0;
        if (!this.loop) return;
        const first = this.slides[0].cloneNode(true);
        const last = this.slides[this.slides.length - 1].cloneNode(true);
        [first, last].forEach((clone) => {
            clone.dataset.zkClone = 'true';
            clone.setAttribute('aria-hidden', 'true');
            clone.querySelectorAll('a, button, input, select, textarea, video, iframe').forEach((item) => item.setAttribute('tabindex', '-1'));
        });
        this.track.prepend(last);
        this.track.append(first);
    }

    handleTransitionEnd(event) {
        if (event.propertyName !== 'transform' || !this.loop) return;
        if (this.physicalIndex === 0) this.physicalIndex = this.slides.length;
        else if (this.physicalIndex === this.slides.length + 1) this.physicalIndex = 1;
        else return;
        this.update(false);
    }

    startAutoplay() {
        if (
            !this.autoplayEnabled ||
            this.prefersReducedMotion.matches ||
            document.hidden ||
            !this.isInView
        ) {
            return;
        }

        this.stopAutoplay();

        this.autoplayTimer = window.setInterval(() => {
            this.next();
        }, this.interval);
    }

    observeVisibility() {
        if (!('IntersectionObserver' in window)) return;
        this.visibilityObserver = new IntersectionObserver(([entry]) => {
            this.isInView = entry.isIntersecting;
            this.isInView ? this.startAutoplay() : this.stopAutoplay();
        }, { rootMargin: '200px 0px' });
        this.visibilityObserver.observe(this.root);
    }

    stopAutoplay() {
        if (this.autoplayTimer) {
            window.clearInterval(this.autoplayTimer);
            this.autoplayTimer = null;
        }
    }

    restartAutoplay() {
        this.stopAutoplay();
        this.startAutoplay();
    }

    handleMouseEnter() {
        this.stopAutoplay();
    }

    handleMouseLeave() {
        if (!this.isDragging) {
            this.startAutoplay();
        }
    }

    handleFocusIn() {
        this.stopAutoplay();
    }

    handleFocusOut(event) {
        if (!this.root.contains(event.relatedTarget)) {
            this.startAutoplay();
        }
    }

    handleVisibilityChange() {
        if (document.hidden) {
            this.stopAutoplay();
        } else {
            this.startAutoplay();
        }
    }

    destroy() {
        this.stopAutoplay();
        this.visibilityObserver?.disconnect();

        this.root.removeEventListener(
            'pointerdown',
            this.handlePointerDown
        );

        window.removeEventListener(
            'pointermove',
            this.handlePointerMove
        );

        window.removeEventListener(
            'pointerup',
            this.handlePointerUp
        );

        window.removeEventListener(
            'pointercancel',
            this.handlePointerUp
        );

        this.root.removeEventListener(
            'mouseenter',
            this.handleMouseEnter
        );

        this.root.removeEventListener(
            'mouseleave',
            this.handleMouseLeave
        );

        this.root.removeEventListener(
            'focusin',
            this.handleFocusIn
        );

        this.root.removeEventListener(
            'focusout',
            this.handleFocusOut
        );

        document.removeEventListener(
            'visibilitychange',
            this.handleVisibilityChange
        );
        this.track.removeEventListener('transitionend', this.handleTransitionEnd);
        this.track.querySelectorAll('[data-zk-clone]').forEach((clone) => clone.remove());
    }
}

const zerokitHowSliders = new WeakMap();

function initZerokitHowSliders(scope = document) {
    const sliders = scope.querySelectorAll('[data-zk-how-slider]');

    sliders.forEach((slider) => {
        if (zerokitHowSliders.has(slider)) {
            return;
        }

        const instance = new ZerokitHowSlider(slider);
        zerokitHowSliders.set(slider, instance);
    });
}

function destroyZerokitHowSliders(scope) {
    const sliders = scope.querySelectorAll('[data-zk-how-slider]');

    sliders.forEach((slider) => {
        const instance = zerokitHowSliders.get(slider);

        if (!instance) {
            return;
        }

        instance.destroy();
        zerokitHowSliders.delete(slider);
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        initZerokitHowSliders();
    });
} else {
    initZerokitHowSliders();
}


/* ==========================================================================
   Shopify Theme Editor support
   ========================================================================== */

document.addEventListener('shopify:section:load', (event) => {
    initZerokitHowSliders(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
    destroyZerokitHowSliders(event.target);
});

document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest('[data-zk-how-slide]');

    if (!slide) {
        return;
    }

    const slider = slide.closest('[data-zk-how-slider]');

    if (!slider) {
        return;
    }

    const instance = zerokitHowSliders.get(slider);

    if (!instance) {
        return;
    }

    const index = Number.parseInt(
        slide.dataset.slideIndex,
        10
    );

    if (!Number.isNaN(index)) {
        instance.goTo(index);
    }
});
